import { useState, useEffect } from "react";
import { useRouteTitle } from "../hooks/useRouteTitle";
import { MoonIcon, SunIcon, BellIcon, GlobeAltIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { call } from "../api";
import { useAuthState } from "../auth/AuthProvider";
import { collection, query, where, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import { BUILD_INFO } from "../buildInfo";

interface Case {
  id: string;
  title: string;
}

export default function SettingsPage() {
  useRouteTitle("Preferences");
  const { user } = useAuthState();
  const [darkMode, setDarkMode] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [sendDeadlineReminders, setSendDeadlineReminders] = useState(true);
  const [timezone, setTimezone] = useState("America/New_York");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>("");
  const [loadingCases, setLoadingCases] = useState(true);

  // Load preferences from Firestore on mount
  useEffect(() => {
    let isMounted = true;

    async function loadPreferences() {
      if (!user) return;

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (isMounted && userDoc.exists()) {
          const data = userDoc.data();
          if (data.settings) {
            setDarkMode(data.settings.darkMode ?? false);
            setEmailNotifications(data.settings.emailNotifications ?? true);
            setPushNotifications(data.settings.pushNotifications ?? false);
            setSendDeadlineReminders(data.settings.sendDeadlineReminders ?? true);
            setTimezone(data.settings.timezone || "America/New_York");

            if (data.settings.darkMode) {
              document.documentElement.classList.add('dark');
            } else {
              document.documentElement.classList.remove('dark');
            }
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        const savedDarkMode = localStorage.getItem('darkMode') !== 'false';
        setDarkMode(savedDarkMode);
        if (savedDarkMode) document.documentElement.classList.add('dark');
      }
    }

    loadPreferences();
    return () => { isMounted = false; };
  }, [user]);

  // Load user's cases for data import
  useEffect(() => {
    async function loadCases() {
      if (!user) {
        setLoadingCases(false);
        return;
      }

      try {
        const casesRef = collection(db, 'cases');
        const q = query(casesRef, where('memberIds', 'array-contains', user.uid));
        const snapshot = await getDocs(q);

        const userCases = snapshot.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title || 'Untitled Case'
        }));

        setCases(userCases);

        if (userCases.length > 0) {
          setSelectedCaseId(userCases[0].id);
        }
      } catch (error) {
        console.error('Error loading cases:', error);
        toast.error('Failed to load your cases');
      } finally {
        setLoadingCases(false);
      }
    }

    loadCases();
  }, [user]);

  const savePreference = async (key: string, value: any) => {
    if (!user) return;

    // Optimistic update
    if (key === 'darkMode') {
      if (value) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        settings: {
          [key]: value
        }
      }, { merge: true });
    } catch (error) {
      console.error(`Error saving preference ${key}:`, error);
      toast.error('Failed to save preference');
    }
  };

  const handleDarkModeToggle = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    savePreference('darkMode', newValue);
  };

  const handleEmailNotificationsToggle = () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    savePreference('emailNotifications', newValue);
  };
  const handlePushNotificationsToggle = () => {
    const newValue = !pushNotifications;
    setPushNotifications(newValue);
    savePreference('pushNotifications', newValue);
  };

  const handleDeadlineRemindersToggle = () => {
    const newValue = !sendDeadlineReminders;
    setSendDeadlineReminders(newValue);
    savePreference('sendDeadlineReminders', newValue);
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setTimezone(newTimezone);
    savePreference('timezone', newTimezone);
  };

  const handleKmlImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!selectedCaseId) {
      setImportError('Please select a case before importing data');
      return;
    }

    setImporting(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      const file = files[0];
      const kmlContent = await file.text();

      const result = await call('parseKmlData', { caseId: selectedCaseId, kmlContent }) as { success: boolean; locations: number; error?: string };

      if (result.success) {
        setImportSuccess(`Successfully imported ${result.locations} location${result.locations !== 1 ? 's' : ''} from ${file.name}.`);
        toast.success(`Successfully imported ${result.locations} location${result.locations !== 1 ? 's' : ''}`);
        setTimeout(() => setImportSuccess(null), 5000);
      } else {
        setImportError(result.error || 'Failed to import KML file');
        toast.error(result.error || 'Failed to import KML file');
      }
    } catch (err) {
      console.error('KML import error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to import KML file';
      setImportError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleIcsImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!selectedCaseId) {
      setImportError('Please select a case before importing data');
      toast.error('Please select a case before importing data');
      return;
    }

    setImporting(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      const file = files[0];
      const icsContent = await file.text();

      const result = await call('parseIcsData', { caseId: selectedCaseId, icsContent }) as { success: boolean; events: number; error?: string };

      if (result.success) {
        setImportSuccess(`Successfully imported ${result.events} event${result.events !== 1 ? 's' : ''} from ${file.name}.`);
        toast.success(`Successfully imported ${result.events} event${result.events !== 1 ? 's' : ''}`);
        setTimeout(() => setImportSuccess(null), 5000);
      } else {
        setImportError(result.error || 'Failed to import calendar file');
        toast.error(result.error || 'Failed to import calendar file');
      }
    } catch (err) {
      console.error('ICS import error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to import calendar file';
      setImportError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset input
    }
  };

  return (
    <main className="mx-auto max-w-2xl p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-white">Preferences</h1>
        <p className="text-sm text-slate-300">User and application preferences.</p>
      </header>

      {/* Appearance Section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          {darkMode ? (
            <MoonIcon className="h-5 w-5 text-slate-200" />
          ) : (
            <SunIcon className="h-5 w-5 text-slate-200" />
          )}
          <h2 className="text-lg font-medium text-white">Appearance</h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-white">Dark Mode</p>
            <p className="text-sm text-slate-300">Toggle dark mode interface</p>
          </div>
          <button
            onClick={handleDarkModeToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${darkMode ? 'bg-brand-orange' : 'bg-gray-200'
              }`}
            aria-label="Toggle dark mode"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-slate-200 transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
          </button>
        </div>
      </section>

      {/* Notifications Section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <BellIcon className="h-5 w-5 text-slate-200" />
          <h2 className="text-lg font-medium text-white">Notifications</h2>
        </div>
        <p className="text-sm text-slate-300 mb-4">
          Control which events notify you and how — in-app, email, or both — on a per-topic basis.
        </p>
        <a
          href="/settings/notifications"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-700 hover:bg-slate-600 px-4 py-2.5 text-sm font-medium text-slate-100 transition-colors"
        >
          <BellIcon className="h-4 w-4" />
          Manage Notification Preferences
        </a>
      </section>

      {/* Regional Settings Section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <GlobeAltIcon className="h-5 w-5 text-slate-200" />
          <h2 className="text-lg font-medium text-white">Regional Settings</h2>
        </div>

        <div>
          <label htmlFor="timezone" className="block font-medium text-white mb-2">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Phoenix">Arizona Time (MST)</option>
            <option value="America/Anchorage">Alaska Time (AKT)</option>
            <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
          </select>
        </div>
      </section>

      {/* Data Sources Section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <ArrowUpTrayIcon className="h-5 w-5 text-slate-200" />
          <h2 className="text-lg font-medium text-white">Data Sources</h2>
        </div>

        <p className="text-sm text-slate-300 mb-6">
          Import timeline and event data from external sources to enrich your case timeline.
        </p>

        {/* Case Selector */}
        <div className="mb-6">
          <label htmlFor="import-case" className="block font-medium text-white mb-2">
            Select Case for Import
          </label>
          {loadingCases ? (
            <p className="text-sm text-slate-300">Loading cases...</p>
          ) : cases.length === 0 ? (
            <p className="text-sm text-slate-300">No cases available. Create a case first to import data.</p>
          ) : (
            <select
              id="import-case"
              value={selectedCaseId}
              onChange={(e) => setSelectedCaseId(e.target.value)}
              className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
            >
              {cases.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}
        </div>

        {importError && (
          <div className="mb-4 rounded-lg border border-red-600 bg-red-500/10 p-3 text-sm text-red-600">
            {importError}
          </div>
        )}

        {importSuccess && (
          <div className="mb-4 rounded-lg border border-green-600 bg-green-500/10 p-3 text-sm text-green-600">
            {importSuccess}
          </div>
        )}

        <div className="space-y-4">
          {/* Google Maps Timeline Import */}
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-medium text-white">Google Maps Timeline (KML)</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Import location history from Google Maps Timeline exports (.kml format)
                </p>
              </div>
            </div>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-orange/90 disabled:opacity-50 disabled:cursor-not-allowed">
              <ArrowUpTrayIcon className="h-4 w-4" />
              {importing ? 'Importing...' : 'Import KML File'}
              <input
                type="file"
                accept=".kml"
                onChange={handleKmlImport}
                disabled={importing}
                className="sr-only"
              />
            </label>
          </div>

          {/* Calendar Import */}
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-medium text-white">Calendar Events (.ics)</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Import calendar events from .ics files (Google Calendar, Outlook, etc.)
                </p>
              </div>
            </div>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-orange/90 disabled:opacity-50 disabled:cursor-not-allowed">
              <ArrowUpTrayIcon className="h-4 w-4" />
              {importing ? 'Importing...' : 'Import ICS File'}
              <input
                type="file"
                accept=".ics"
                onChange={handleIcsImport}
                disabled={importing}
                className="sr-only"
              />
            </label>
          </div>
        </div>
      </section>

      {/* Data & Privacy Section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <h2 className="text-lg font-medium text-white mb-4">Data & Privacy</h2>

        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Your data is stored securely in Firebase Cloud Firestore and is encrypted both in transit and at rest.
          </p>
          <div className="flex gap-2">
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-brand-orange hover:underline"
            >
              Privacy Policy →
            </a>
            <span className="text-gray-400">|</span>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-brand-orange hover:underline"
            >
              Terms of Service →
            </a>
          </div>
        </div>
      </section>

      {/* Build Information Section - Hidden from UI, available in console */}
      <section className="hidden rounded-xl border border-slate-700 bg-slate-900/50 p-6 shadow-sm">
        <h2 className="text-lg font-medium text-white mb-4">Build Information</h2>

        <div className="space-y-3 font-mono text-sm">
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <span className="text-slate-400">Version:</span>
            <span className="text-slate-200">{BUILD_INFO.appVersion}</span>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <span className="text-slate-400">Git SHA:</span>
            <span className="text-slate-200 break-all">{BUILD_INFO.gitSha}</span>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <span className="text-slate-400">Branch:</span>
            <span className="text-slate-200">{BUILD_INFO.gitBranch}</span>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <span className="text-slate-400">Build Time:</span>
            <span className="text-slate-200">{BUILD_INFO.buildTimeUtc}</span>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <span className="text-slate-400">Environment:</span>
            <span className="text-slate-200">{BUILD_INFO.environment}</span>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-400">
              Build tag: <span className="text-slate-300">{BUILD_INFO.buildTag}</span>
            </p>
            <p className="text-xs text-slate-500 mt-2">
              This information is cryptographically tied to the deployed artifact.
              Verify at <span className="text-brand-orange">/version.json</span>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
